<template>
  <div class="form-group" :class="{'has-danger': error, 'multiline-select': multilineSelected, 'is-disabled-cursor': disabled}">
    <label v-if="label">
      {{ label }}
      <span v-if="required" class="text-danger">*</span>
    </label>
    <v-select
      :value="selectedOption"
      :options="filteredOptions"
      :selectable="option => !option.$isHeader"
      :filterable="false"
      :reduce="option => option.codigo"
      :multiple="multiple"
      label="label"
      :placeholder="'Pesquisar por código ou nome...'"
      :disabled="disabled"
      :clearable="clearable"
      @input="onInput"
      @search="onSearch"
    >
      <template #option="option">
        <div v-if="option.$isHeader" class="lna-section-header">
          {{ option.label }}
        </div>
        <div v-else>
          {{ option.label }}
        </div>
      </template>
      <template #selected-option="option">
        {{ option.label }}
      </template>
      <template #no-options>
        Nenhum ato encontrado
      </template>
    </v-select>
    <div v-if="error" class="invalid-feedback" style="display: block;">{{ error }}</div>
  </div>
</template>

<script>
import VueSelect from 'vue-select';
import LnaSreiBusiness from "@/business/crud/LnaSreiBusiness";

export default {
  name: "SelectLnaSrei",
  components: {
    'v-select': VueSelect,
  },
  props: {
    value: { type: [String, Array], default: null },
    livro: { type: Number, required: true },
    label: String,
    required: Boolean,
    error: String,
    disabled: Boolean,
    allowedCodes: { type: Array, default: null },
    multiple: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    multilineSelected: { type: Boolean, default: false },
  },
  data() {
    return {
      atos: [],
      allOptions: [],
      search: '',
    };
  },
  computed: {
    atosFiltrados() {
      if (!this.allowedCodes || !this.allowedCodes.length) return this.atos;
      const set = new Set(this.allowedCodes);
      return this.atos.filter(a => set.has(a.codigo));
    },
    selectedOption() {
      if (this.multiple) {
        if (!this.value || !this.value.length) return [];
        const set = new Set(this.value);
        return this.allOptions.filter(o => !o.$isHeader && set.has(o.codigo));
      }
      if (!this.value) return null;
      const fromAllOptions = this.allOptions.find(o => !o.$isHeader && o.codigo === this.value);
      if (fromAllOptions) return fromAllOptions;
      const fromAtos = this.atos.find(a => a.codigo === this.value);
      return fromAtos ? this.toOption(fromAtos) : null;
    },
    filteredOptions() {
      if (!this.search) return this.allOptions;
      const term = this.search.toLowerCase();
      const matchingAtos = this.atosFiltrados.filter(a =>
        a.codigo.toLowerCase().includes(term) || a.nome.toLowerCase().includes(term)
      );
      const sections = [...new Set(matchingAtos.map(a => a.nomeSecao))];
      const result = [];
      for (const nomeSecao of sections) {
        const header = this.allOptions.find(o => o.$isHeader && o.nomeSecao === nomeSecao);
        if (header) result.push(header);
        result.push(...matchingAtos.filter(a => a.nomeSecao === nomeSecao).map(a => this.toOption(a)));
      }
      return result;
    },
  },
  watch: {
    allowedCodes() {
      this.buildOptions();
    },
  },
  async mounted() {
    await this.loadAtos();
  },
  methods: {
    async loadAtos() {
      this.atos = await LnaSreiBusiness.getByLivro(this.livro);
      this.buildOptions();
    },
    buildOptions() {
      const options = [];
      const sections = [...new Set(this.atosFiltrados.map(a => a.nomeSecao))];
      for (const nomeSecao of sections) {
        const firstAto = this.atosFiltrados.find(a => a.nomeSecao === nomeSecao);
        options.push({
          $isHeader: true,
          label: firstAto?.nomeSecao || '',
          nomeSecao,
          codigo: `header_${nomeSecao}`,
        });
        const sectionAtos = this.atosFiltrados.filter(a => a.nomeSecao === nomeSecao);
        for (const ato of sectionAtos) {
          options.push(this.toOption(ato));
        }
      }
      this.allOptions = options;
    },
    toOption(ato) {
      return {
        $isHeader: false,
        codigo: ato.codigo,
        nome: ato.nome,
        label: `${ato.codigo} – ${ato.nome}`,
      };
    },
    onInput(val) {
      this.$emit('input', val);
    },
    onSearch(search) {
      this.search = search;
    },
  },
};
</script>

<style lang="scss" scoped>
.lna-section-header {
  font-weight: bold;
  background-color: #f8f9fa;
  font-size: 0.85em;
  padding: 6px 12px;
  color: #6c757d;
  margin: 0 -20px;
  padding-left: 20px;
  padding-right: 20px;
}

::v-deep .vs__dropdown-toggle {
  border: 1px solid var(--asgard-input);
}

.form-group.has-danger ::v-deep .vs__dropdown-toggle {
  border-color: #dc3545;
}

.multiline-select {
  ::v-deep .vs__dropdown-toggle {
    height: auto;
    min-height: 38px;
  }

  ::v-deep .vs__selected-options {
    flex-wrap: wrap;
  }

  ::v-deep .vs__selected {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    max-width: 100%;
  }
}

.is-disabled-cursor,
.is-disabled-cursor ::v-deep * {
  cursor: not-allowed !important;
}

</style>
